import React, { useEffect, useState } from 'react';

import { isEmpty } from 'lodash-es';
import { useIntl } from 'react-intl';

import { useCartButtonText } from 'components/bottom-service-mode/hooks/use-cart-button-text';
import LoyaltyPointsIcon from 'components/icons/loyalty-points-icon';
import LoadingAnimation from 'components/loading-animation';
import Modal, { ModalSize } from 'components/modal';
import SelectionIndicator from 'components/selection-indicator';
import { Size } from 'components/selection-indicator/constants';
import { BenefitType, IBenefitSwap } from 'generated/graphql-gateway';
import { useAllItemsQuery } from 'generated/sanity-graphql';
import { useLoyaltyContext } from 'state/loyalty';
import { useOrderContext } from 'state/order';

import {
  CloseButton,
  Container,
  Header,
  ItemImage,
  MainTitle,
  NoThanks,
  SingleItem,
  SingleItemContent,
  Subtitle,
  UpsizeButton,
  UpsizeItems,
} from './modal-upsize.styled';

const ModalUpsize = () => {
  const { cartButtonText } = useCartButtonText();
  const { upsizeAvailable, offers, resetUpsizeAvailability } = useLoyaltyContext();
  const { swapItems } = useOrderContext();

  const swappableItemsMap = offers.reduce((acc, offer) => {
    if (offer.benefits?.length) {
      offer.benefits.forEach((benefit: IBenefitSwap) => {
        if (benefit.type === BenefitType.SWAP) {
          acc[benefit.value.to] = { ...benefit.value, offerId: offer.id, offerType: offer.type };
        }
      });
    }

    return acc;
  }, {});

  const { loading, data } = useAllItemsQuery({
    variables: {
      where: {
        _id_in: Object.keys(swappableItemsMap),
      },
    },
    skip: isEmpty(swappableItemsMap),
  });

  const [swapTo, setSelectedSwapTo] = useState<{ [key: string]: any }>({});
  const { formatMessage } = useIntl();
  const modalHeading = formatMessage({ id: 'freeRewardsUpsize' });

  useEffect(() => {
    if (data?.allItems?.length) {
      setSelectedSwapTo(data.allItems[0]);
    }
  }, [data, setSelectedSwapTo]);

  const renderSelectableItems = () => {
    if (loading) {
      return <LoadingAnimation fillColor={Styles.color.red} />;
    }

    return data?.allItems ? (
      <UpsizeItems>
        {data?.allItems?.map(cmsItem => {
          return (
            <SingleItem onClick={() => setSelectedSwapTo(cmsItem)} key={cmsItem._id}>
              <SingleItemContent>
                {cmsItem.image && (
                  <ItemImage alt={cmsItem.name?.locale || ''} image={cmsItem.image} />
                )}
                <div>
                  <h4>{cmsItem.name?.locale}</h4>
                  <p>{cmsItem.nutrition?.calories} Cal</p>
                </div>
              </SingleItemContent>
              <SelectionIndicator checked={swapTo._id === cmsItem._id} size={Size.REGULAR} />
            </SingleItem>
          );
        })}
      </UpsizeItems>
    ) : null;
  };

  if (!upsizeAvailable) {
    return null;
  }

  const onUpsizeClickHandler = () => {
    const { from: swapFrom, ...selectedOffer } = swappableItemsMap[swapTo._id];
    swapItems(swapFrom, swapTo, selectedOffer);
    resetUpsizeAvailability();
  };

  return (
    <Modal
      allowsDismiss
      closeButton={() => <CloseButton onClick={resetUpsizeAvailability} />}
      onDismiss={resetUpsizeAvailability}
      size={ModalSize.SMALL}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Upsize available',
        modalMessage: modalHeading,
      }}
    >
      <Container>
        <Header>
          <LoyaltyPointsIcon />
          {modalHeading}
        </Header>
        <div>
          <MainTitle>{formatMessage({ id: 'upsizeForFree' })}</MainTitle>
          <span> {formatMessage({ id: 'upsizeEligible' })}</span>
        </div>
        <div>
          <Subtitle>{formatMessage({ id: 'upsizeSelection' })}</Subtitle>
          {renderSelectableItems()}
        </div>
        <div>
          <UpsizeButton
            disabled={loading || !data}
            onClick={onUpsizeClickHandler}
            aria-label="upsize-btn"
          >
            {formatMessage({ id: 'freeUpsize' }, { amount: cartButtonText })}
          </UpsizeButton>
          <NoThanks
            onClick={resetUpsizeAvailability}
            aria-label={formatMessage({ id: 'noThanks' })}
          >
            {formatMessage({ id: 'noThanks' })}
          </NoThanks>
        </div>
      </Container>
    </Modal>
  );
};

export default ModalUpsize;
